// One solution (02-DESIGN/14-solutions/01-the-manifest.md § the console:
// Solutions, § install, § upgrade, § removal): installed, its page in tabs
// (02-DESIGN/03-console/01-the-console.md § tabs; the moves, page by page) —
// *Overview*, its state, its version, its workloads with their status and its
// pages; *What it may do*, the declaration counted first; *Settings*, its
// tenant settings and its secrets for admins; *Update* when a
// newer revision exists with the difference where it widens, *Remove*
// confirmed twice; not installed, its declaration from the catalogue with
// the one primary action *Install*, which is the consent. After the
// solutions service's platform half the console runs the account-local
// steps, and shows the install incomplete — with *Finish install* naming the
// missing step — until everything it can read is there. A licensed
// solution shows *licensed until …* and the builder's terms as given under
// *License*; past its expiry *license expired* with the hint, and *Update*
// asks for a new one, carried in the upgrade's body
// (02-DESIGN/17-plans/03-solution-licenses.md § the record, the page and the end).

import { useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { isRefusal } from "../../platform/bus.ts";
import { usePage } from "../../platform/session.tsx";
import {
  Button,
  Confirm,
  NotAllowed,
  Notice,
  PageHead,
  Pending,
  Pill,
  TextArea,
  TextInput,
  Field,
  useLoad,
} from "../../ui/components.tsx";
import { Table } from "../../ui/table.tsx";
import { Tabs, useTab, type TabSpec } from "../../ui/tabs.tsx";
import {
  Code,
  DOCUMENT_SETTINGS,
  Installer,
  secretPath,
  SERVICE,
  Subjects,
  type Manifest,
  type Record_,
  type InstallRequest,
} from "../../wire/solutions.ts";
import { listSubject, SERVICE as WORKLOADS, type ListReply } from "../../wire/workloads.ts";
import { useSurface } from "../surface.tsx";
import { plainState } from "../workloads/state.ts";
import { Declaration } from "./Declaration.tsx";
import { FieldInput, parseValue, valueText } from "./fields.tsx";
import { readInstalled } from "./installed.ts";
import { NotInstalled, useWorkspace } from "./NotInstalled.tsx";
import { PageRedirectNote } from "./PageRedirectNote.tsx";
import { missingOf, readFacts, removeSteps, runSteps } from "./install.ts";
import { panelsShown, personPanels, settingsAsked, type Ids } from "./panel.ts";
import { pageLinks } from "./pages.ts";
import { pageOffered } from "../../roles/offers.ts";
import { useGrants } from "./sends.ts";
import { consentLine, REMOVE_CONSEQUENCE, stateWords, tenantSecrets } from "./words.ts";
import { hintSentence, isExpired, licenseRefusal, licenseWords, termsRows } from "./license.ts";

export function SolutionPage() {
  const { name = "" } = useParams();
  const s = useSurface();
  // An install's step that failed, carried onto the installed page, which
  // the install turns this page into: the refusal is the person's to read,
  // never replaced by the completeness the page computes afterwards.
  const [stepErr, setStepErr] = useState<unknown>(undefined);
  const [load, reload] = useLoad(`solution/${name}`, async () => {
    try {
      return { installed: await s.bus.call<Record_>(SERVICE, Subjects.installedGet(name), undefined) };
    } catch (err) {
      if (!isRefusal(err, Code.notFound)) {
        throw err;
      }
      return { installed: undefined };
    }
  });
  // The crumb names the solution by its display name, as its title and every other crumb do.
  const crumbOf = (display: string) => (
    <>
      <Link to={tenantPath(s.tenant, "solutions")}>Solutions</Link>
      <span>/</span>
      <span>{display}</span>
    </>
  );
  const crumb = crumbOf(
    load.state === "ready" && load.value.installed?.manifest !== undefined
      ? load.value.installed.manifest.display
      : name,
  );
  if (load.state === "pending") {
    return (
      <>
        <div className="crumb">{crumb}</div>
        <Pending what="Reading the solution" />
      </>
    );
  }
  if (load.state === "refused") {
    return (
      <>
        <PageHead crumb={crumb} title={name} />
        <NotAllowed err={load.err} what="This solution" />
      </>
    );
  }
  const r = load.value.installed;
  if (r === undefined || r.manifest === undefined) {
    return (
      <NotInstalled
        name={name}
        crumb={crumbOf}
        onInstalled={(err) => {
          setStepErr(err);
          reload();
        }}
      />
    );
  }
  return <Installed r={r} m={r.manifest} crumb={crumb} reload={reload} stepErr={stepErr} />;
}

function Installed({
  r,
  m,
  crumb,
  reload,
  stepErr,
}: {
  r: Record_;
  m: Manifest;
  crumb: ReactNode;
  reload: () => void;
  /** The step the install stopped at, when it was this page's install that just ran. */
  stepErr?: unknown;
}) {
  const s = useSurface();
  const { platform } = usePage();
  const navigate = useNavigate();
  const location = useLocation();
  const admin = s.offered("solutions:manage");
  const ws = useWorkspace(admin);
  const required = r.installer === Installer.steward;
  const [tick, setTick] = useState(0);
  const [facts] = useLoad(
    `solution/facts/${r.name}/${r.revision}/${String(admin)}`,
    () => (admin ? readFacts(s.bus, m) : Promise.resolve(undefined)),
    tick,
  );
  const missing =
    facts.state === "ready" && facts.value !== undefined ? missingOf(m, facts.value) : undefined;
  const st = stateWords(r, missing, ws.parent);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(stepErr);
  const [note, setNote] = useState<string | undefined>(undefined);
  const [license, setLicense] = useState("");
  const token = license.trim();
  const expired = isExpired(r.license);
  const hint = m.license?.hint;
  const licenseSaid = err !== undefined ? licenseRefusal(err, m.display, hint) : undefined;
  // A new license is asked for past the held one's expiry, or once an update was refused for its license.
  const asking = admin && !required && (expired || licenseSaid !== undefined);
  const tabs: TabSpec<SolutionTab>[] = [
    { id: "overview", label: "Overview" },
    { id: "may", label: "What it may do" },
    ...(admin ? [{ id: "settings" as const, label: "Settings" }] : []),
  ];
  // A link may carry the tab it came from: a role's crumb returns to *What it may do*.
  const [tab, setTab] = useTab(tabs, solutionTabOf(location.state));
  const refresh = () => {
    setTick((t) => t + 1);
    reload();
  };
  const run = (what: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    setErr(undefined);
    setNote(undefined);
    what().then(
      () => {
        setBusy(false);
        setNote(done);
        void readInstalled(s.bus).catch(() => undefined);
        refresh();
      },
      (e: unknown) => {
        setBusy(false);
        setErr(e);
        refresh();
      },
    );
  };
  const finish = () => {
    run(() => runSteps(s.bus, m, { solo: ws.solo, images: platform.images }), "The install is finished.");
  };
  const newer = r.latest !== undefined && r.latest > r.revision;
  const widens = r.widens ?? [];
  const update = () => {
    run(async () => {
      // Past the license's expiry an update at the revision installed renews it.
      const body: InstallRequest = { revision: r.latest ?? r.revision };
      if (token !== "") {
        body.license = token;
      }
      const next = await s.bus.call<Record_>(SERVICE, Subjects.upgrade(r.name), body, 60000);
      setLicense("");
      await runSteps(s.bus, next.manifest ?? m, { solo: ws.solo, images: platform.images });
    }, "Updated.");
  };
  const remove = () => {
    setBusy(true);
    setErr(undefined);
    s.bus
      .call<Record_>(SERVICE, Subjects.remove(r.name), undefined, 60000)
      .then(() => removeSteps(s.bus, m, ws.solo))
      .then(() => readInstalled(s.bus))
      .then(
        () => {
          void navigate(tenantPath(s.tenant, "solutions"));
        },
        (e: unknown) => {
          setBusy(false);
          setErr(e);
        },
      );
  };
  return (
    <>
      <PageHead
        crumb={crumb}
        title={m.display}
        line={consentLine(r, ws.parent)}
        action={
          tab === "overview" && admin && !required && (newer || expired) ? (
            <Button kind="primary" onClick={update} disabled={busy || (asking && token === "")}>
              {widens.length > 0 ? "Consent and update" : "Update"}
            </Button>
          ) : undefined
        }
      />
      {err !== undefined &&
        (licenseSaid !== undefined ? (
          <Notice kind="bad" title="Update: refused">
            {licenseSaid}
          </Notice>
        ) : (
          <NotAllowed err={err} />
        ))}
      {note !== undefined && <Notice kind="ok">{note}</Notice>}

      <Tabs label={m.display} tabs={tabs} value={tab} onChange={setTab}>
        {tab === "overview" && (
          <>
            <section className="section card">
              <div className="row">
                <Pill tone={st.tone}>{st.word}</Pill>
                {m.version !== undefined && m.version !== "" && (
                  <span className="small muted">version {m.version}</span>
                )}
                {admin && missing !== undefined && missing.length > 0 && (
                  <Button size="sm" onClick={finish} disabled={busy || !ws.ready}>
                    Finish install
                  </Button>
                )}
              </div>
              {missing !== undefined && missing.length > 1 && (
                <ul className="plain small" style={{ marginTop: 12 }}>
                  {missing.map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              )}
              {admin && newer && !required && widens.length > 0 && (
                <Notice title={`Release ${String(r.latest)} asks for more`}>
                  <ul className="plain">
                    {widens.map((w) => (
                      <li key={w}>{w}</li>
                    ))}
                  </ul>
                  Updating is your consent to it.
                </Notice>
              )}
            </section>

            {(r.license !== undefined || asking) && (
              <section className="section">
                <div className="section-head">
                  <h2>License</h2>
                </div>
                <div className="card stack">
                  {r.license !== undefined && (
                    <p>
                      {expired ? <Pill tone="bad">license expired</Pill> : licenseWords(r.license)}
                      {expired && hint !== undefined && (
                        <span className="small muted"> {hintSentence(hint)}</span>
                      )}
                    </p>
                  )}
                  {termsRows(r.license?.terms).length > 0 && (
                    <dl className="kv">
                      {termsRows(r.license?.terms).map((t) => (
                        <Term key={t.name} name={t.name} value={t.value} />
                      ))}
                    </dl>
                  )}
                  {asking && (
                    <Field
                      label="License"
                      hint={`A new license from ${m.display}'s publisher, pasted whole; then Update.`}
                    >
                      <TextArea value={license} onChange={setLicense} rows={3} mono disabled={busy} />
                    </Field>
                  )}
                </div>
              </section>
            )}
            {admin && <PageRedirectNote m={m} workspace={s.name} />}
            {admin && <Workloads r={r} />}
            <Panels r={r} m={m} />

            {admin && !required && (
              <section className="section">
                <div className="row" style={{ justifyContent: "flex-end" }}>
                  <Confirm
                    label="Remove"
                    consequence={REMOVE_CONSEQUENCE}
                    onConfirm={remove}
                    disabled={busy}
                  />
                </div>
              </section>
            )}
          </>
        )}
        {tab === "may" && (
          <Declaration
            m={m}
            from={{ publisher: r.publisher, revision: r.revision }}
            ids={r.workloads ?? {}}
          />
        )}
        {tab === "settings" && admin && (
          <>
            <SettingsForm m={m} ids={r.workloads ?? {}} disabled={busy} />
            <Secrets
              m={m}
              onStored={refresh}
              held={facts.state === "ready" ? facts.value?.secrets : undefined}
            />
          </>
        )}
      </Tabs>
      {required && (
        <p className="small faint">
          {ws.parent ?? "The parent"}'s blueprint requires it: it is removed when the blueprint stops naming
          it.
        </p>
      )}
    </>
  );
}

/** SolutionTab is one of a solution's tabs. */
export type SolutionTab = "overview" | "may" | "settings";

/** MAY_TAB is the tab *What it may do*, which a role's page links back to. */
export const MAY_TAB: SolutionTab = "may";

/** solutionTabOf is the tab a link asked a solution's page to open on, `{ tab }`, or nothing. */
export function solutionTabOf(state: unknown): SolutionTab | undefined {
  const t = (state as { tab?: unknown } | null)?.tab;
  return t === "overview" || t === "may" || t === "settings" ? t : undefined;
}

/** Term is one of the builder's terms as given: shown, never read. */
function Term({ name, value }: { name: string; value: string }) {
  return (
    <>
      <dt>{name}</dt>
      <dd>{value}</dd>
    </>
  );
}

function Workloads({ r }: { r: Record_ }) {
  const s = useSurface();
  const [list] = useLoad(`solution/workloads/${r.name}/${r.updated}`, () =>
    s.bus.call<ListReply>(WORKLOADS, listSubject(), undefined),
  );
  const ids = Object.entries(r.workloads ?? {});
  if (ids.length === 0) {
    return null;
  }
  const stateOf = (id: string) => {
    const w = list.state === "ready" ? list.value.workloads.find((x) => x.id === id) : undefined;
    return w !== undefined ? plainState(w) : undefined;
  };
  return (
    <section className="section">
      <div className="section-head">
        <h2>Workloads</h2>
      </div>
      <Table
        label="Workloads"
        rows={ids}
        rowKey={([role]) => role}
        sort={{ key: "role", dir: "asc" }}
        // A workload's page is an admin's (hq decision 0047): a member reads the row, unlinked.
        to={([, id]) => (s.offered("workloads:list:any") ? tenantPath(s.tenant, "workloads", id) : undefined)}
        columns={[
          {
            key: "role",
            header: "Workload",
            width: "minmax(0, 2fr)",
            value: ([role]) => role,
            // The id is on the workload's page, not here (the moves, page by page).
            cell: ([role]) => <span className="cell-main">{role}</span>,
          },
          {
            key: "status",
            header: "Status",
            width: "140px",
            facet: "Status",
            value: ([, id]) => stateOf(id)?.word ?? "",
            cell: ([, id]) => {
              const p = stateOf(id);
              return p !== undefined ? <Pill tone={p.tone}>{p.word}</Pill> : null;
            },
          },
        ]}
      />
    </section>
  );
}

function Panels({ r, m }: { r: Record_; m: Manifest }) {
  const s = useSurface();
  // Its pages as links (§ conventions: the tabs of a solution's page): the
  // observability solution's admin panels are the tabs of Audit & usage
  // (hq decision 0048), and link there on their tab.
  const admin = s.offered("solutions:manage");
  const grants = useGrants(s.bus, admin);
  const ids = r.workloads ?? {};
  const links = pageLinks(
    s.tenant,
    r.name,
    m,
    panelsShown(m, ids, admin, grants),
    personPanels(m, ids, admin, grants),
    pageOffered(s.role, "audit"),
  );
  if (links.length === 0) {
    return null;
  }
  return (
    <section className="section">
      <div className="section-head">
        <h2>Its pages</h2>
      </div>
      <div className="card slim">
        <div className="list">
          {links.map((l) => (
            <Link className="li" key={l.key} to={l.to} state={l.state}>
              <span className="grow">
                <span className="cell-main">{l.title}</span>
                {l.line !== undefined && <span className="cell-sub">{l.line}</span>}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/** SettingsForm is the solution's tenant-scoped settings, for admins, saved whole under the revision guard. */
function SettingsForm({ m, ids, disabled }: { m: Manifest; ids: Ids; disabled: boolean }) {
  const s = useSurface();
  // A setting that declares `sends` is asked only where a grant sends one of
  // them, and one that declares `receives` only where the install record
  // binds a role on grant receiving one of them; its value stays in the
  // document either way (hq issues 185, 189).
  const grants = useGrants(s.bus, true);
  const fields = settingsAsked(
    m,
    (m.settings ?? []).filter((x) => x.scope === "tenant" && x.secret !== true),
    grants,
    ids,
  );
  const [tick, setTick] = useState(0);
  const [doc] = useLoad(
    `solution/settings/${m.name}`,
    async () => {
      try {
        return await s.bus.solutionConfig().get(m.name, DOCUMENT_SETTINGS);
      } catch (err) {
        if (isRefusal(err, "not-found")) {
          return undefined;
        }
        throw err;
      }
    },
    tick,
  );
  const [draft, setDraft] = useState<Record<string, string> | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  if (fields.length === 0) {
    return null;
  }
  if (doc.state === "pending") {
    return <Pending what="Reading its settings" />;
  }
  if (doc.state === "refused") {
    return <NotAllowed err={doc.err} what="Its settings" />;
  }
  let held: Record<string, unknown> = {};
  try {
    held = doc.value?.value !== undefined ? (JSON.parse(doc.value.value) as Record<string, unknown>) : {};
  } catch {
    held = {};
  }
  const values: Record<string, string> =
    draft ?? Object.fromEntries(fields.map((f) => [f.name, valueText(held[f.name] ?? f.default)]));
  const save = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    const out: Record<string, unknown> = { ...held };
    for (const f of fields) {
      const v = parseValue(f.kind, values[f.name] ?? "");
      if (v === undefined) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- a cleared field leaves the document
        delete out[f.name];
      } else {
        out[f.name] = v;
      }
    }
    setBusy(true);
    setErr(undefined);
    s.bus
      .solutionConfig()
      .set(m.name, DOCUMENT_SETTINGS, JSON.stringify(out, null, 2) + "\n", doc.value?.revision)
      .then(
        () => {
          setBusy(false);
          setDraft(undefined);
          setTick((t) => t + 1);
        },
        (x: unknown) => {
          setBusy(false);
          setErr(x);
        },
      );
  };
  return (
    <section className="section">
      <div className="section-head">
        <h2>Settings</h2>
        <span className="small faint">
          what its workloads read while they run; a change reaches them without a restart
        </span>
      </div>
      <form className="card stack" onSubmit={save}>
        {err !== undefined && <NotAllowed err={err} />}
        {fields.map((f) => (
          <FieldInput
            key={f.name}
            f={f}
            value={values[f.name] ?? ""}
            disabled={disabled || busy}
            onChange={(v) => {
              setDraft({ ...values, [f.name]: v });
            }}
          />
        ))}
        <div className="form-actions">
          {doc.value !== undefined && <span className="small faint">version {doc.value.revision}</span>}
          <Button kind="primary" type="submit" disabled={busy || disabled || draft === undefined}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </form>
    </section>
  );
}

/** Secrets are what an admin stores under the solution's prefix: typed once, never shown back. */
function Secrets({
  m,
  held,
  onStored,
}: {
  m: Manifest;
  held: ReadonlySet<string> | undefined;
  onStored: () => void;
}) {
  const s = useSurface();
  const secrets = tenantSecrets(m);
  const [typed, setTyped] = useState<Record<string, string>>({});
  const [err, setErr] = useState<unknown>(undefined);
  if (secrets.length === 0) {
    return null;
  }
  const store = (name: string) => {
    const value = typed[name] ?? "";
    if (value === "") {
      return;
    }
    setErr(undefined);
    s.bus
      .vault()
      .store(secretPath(m.name, name), value)
      .then(
        () => {
          setTyped({ ...typed, [name]: "" });
          onStored();
        },
        (x: unknown) => {
          setErr(x);
        },
      );
  };
  return (
    <section className="section">
      <div className="section-head">
        <h2>Secrets</h2>
        <span className="small faint">
          stored in {s.name}'s vault; its workloads read them, nobody else does
        </span>
      </div>
      <div className="card stack">
        {err !== undefined && <NotAllowed err={err} what="Store" />}
        {secrets.map((x) => (
          <Field
            key={x.name}
            label={`${x.name}${held?.has(x.name) === true ? " — set" : x.required ? " — required, not set" : " — not set"}`}
            hint={x.hint}
          >
            <div className="row">
              <TextInput
                type="password"
                value={typed[x.name] ?? ""}
                onChange={(v) => {
                  setTyped({ ...typed, [x.name]: v });
                }}
              />
              <Button
                size="sm"
                disabled={(typed[x.name] ?? "") === ""}
                onClick={() => {
                  store(x.name);
                }}
              >
                {held?.has(x.name) === true ? "Replace" : "Store"}
              </Button>
            </div>
          </Field>
        ))}
      </div>
    </section>
  );
}
